import { useCallback, useEffect, useRef, useState } from "react";
import http, { formatApiErrorDetail } from "@/api";
import { MailPlus, X } from "lucide-react";
import {
  REISSUE_DEFAULT_DAYS,
  reissuePreviewText,
  reissueSummaryLines,
  skipReason,
} from "@/lib/registrationEmail";

// Bulk re-invitation of suppliers whose latest invitation expired (spec 002, RF-9, D15).
// The backend decides who is eligible; this shows the preview, asks for confirmation and shows the summary.
export default function ReissueInvitationsModal({ onClose }) {
  const [days, setDays] = useState(REISSUE_DEFAULT_DAYS);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null); // summary after confirming
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState("");
  const confirming = useRef(false); // blocks a second click before the first re-render

  const request = useCallback((dryRun) => http.post(
    "/admin/invitations/reissue-expired", null,
    { params: { dry_run: dryRun, expired_within_days: Number(days) || REISSUE_DEFAULT_DAYS } },
  ), [days]);

  const loadPreview = useCallback(async () => {
    setLoading(true); setErr(""); setResult(null);
    try {
      const { data } = await request(true);
      setPreview(data);
    } catch (e) {
      setPreview(null);
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, [request]);

  // The first preview is requested once, when the modal opens; later ones are explicit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPreview(); }, []);

  const confirm = async () => {
    if (confirming.current) return;
    confirming.current = true;
    setRunning(true); setErr("");
    try {
      const { data } = await request(false);
      setResult(data);
      setPreview(null);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { confirming.current = false; setRunning(false); }
  };

  const eligible = preview?.eligible || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={onClose} data-testid="reissue-modal">
      <div className="sketch-border bg-[var(--cream)] p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="font-hand text-lg text-[var(--lala-blue)]">invitaciones</div>
            <h3 className="font-display text-2xl">Reinvitar proveedores con invitación vencida</h3>
            <p className="text-xs text-[var(--ink-soft)] mt-1 max-w-lg">
              Útil cuando una pausa del envío de correos dejó invitaciones vencidas antes de salir. Cada proveedor sin
              cuenta recibe una invitación nueva con su propio correo; la anterior deja de funcionar. Revisa la lista
              antes de confirmar.
            </p>
          </div>
          <button onClick={onClose} className="text-[var(--ink-soft)] hover:text-[var(--ink)]" data-testid="reissue-close"><X size={20} /></button>
        </div>

        {!result && (
          <div className="flex items-end gap-2 mb-3">
            <label className="text-xs text-[var(--ink-soft)]">
              Vencidas en los últimos (días)
              <input type="number" min="1" max="365" value={days} onChange={(e) => setDays(e.target.value)}
                className="block mt-1 border border-[var(--line)] rounded-lg px-3 py-1.5 text-sm w-28 bg-white" data-testid="reissue-days" />
            </label>
            <button onClick={loadPreview} disabled={loading || running} className="sketch-pill px-3 py-1.5 text-sm font-semibold disabled:opacity-40" data-testid="reissue-refresh">
              Actualizar vista previa
            </button>
          </div>
        )}

        {err && <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="reissue-error">{err}</div>}
        {loading && <div className="py-4 text-center text-[var(--ink-soft)]">Calculando vista previa…</div>}

        {preview && !loading && (
          <div data-testid="reissue-preview">
            <div className="text-sm mb-2" data-testid="reissue-preview-text">{reissuePreviewText(preview)}</div>
            {eligible.length > 0 && (
              <ul className="text-sm border border-[var(--line)] rounded-lg divide-y divide-[var(--line)] max-h-64 overflow-y-auto bg-[var(--cream-soft)]" data-testid="reissue-list">
                {eligible.map((s) => (
                  <li key={s.supplier_id} className="px-3 py-2 flex justify-between gap-3" data-testid={`reissue-row-${s.supplier_id}`}>
                    <span className="font-semibold">{s.supplier_name}</span>
                    <span className="text-xs text-[var(--ink-soft)]">{s.contact_email || "sin correo de contacto"} · venció {new Date(s.expired_at).toLocaleDateString("es-MX")}</span>
                  </li>
                ))}
              </ul>
            )}
            {preview.skipped.length > 0 && (
              <details className="mt-3 text-xs text-[var(--ink-soft)]" data-testid="reissue-skipped-preview">
                <summary className="cursor-pointer">{preview.skipped.length} omitidos</summary>
                <ul className="mt-1 space-y-0.5">
                  {preview.skipped.map((r) => <li key={r.supplier_id}>{r.supplier_name} — {skipReason(r.reason)}</li>)}
                </ul>
              </details>
            )}
            <button onClick={confirm} disabled={running || eligible.length === 0}
              className="btn-primary text-sm mt-4 disabled:opacity-40" data-testid="reissue-confirm">
              <MailPlus size={14} /> {running ? "Reinvitando…" : `Confirmar y reinvitar a ${eligible.length}`}
            </button>
          </div>
        )}

        {result && (
          <div data-testid="reissue-result">
            <div className="text-sm bg-green-50 border border-green-200 text-green-900 rounded-lg p-3">
              <ul className="list-disc pl-5" data-testid="reissue-summary">
                {reissueSummaryLines(result).map((line) => <li key={line}>{line}</li>)}
              </ul>
            </div>
            {result.without_email.length > 0 && (
              <div className="mt-3 text-sm bg-amber-50 border border-amber-300 text-amber-900 rounded-lg p-3" data-testid="reissue-without-email">
                <div className="font-semibold mb-1">Reinvitados sin correo registrado — genera una invitación nueva a cada uno:</div>
                <ul className="list-disc pl-5">{result.without_email.map((r) => <li key={r.supplier_id}>{r.supplier_name}</li>)}</ul>
              </div>
            )}
            {result.skipped.length > 0 && (
              <details className="mt-3 text-xs text-[var(--ink-soft)]">
                <summary className="cursor-pointer">Omitidos</summary>
                <ul className="mt-1 space-y-0.5">{result.skipped.map((r) => <li key={r.supplier_id}>{r.supplier_name} — {skipReason(r.reason)}</li>)}</ul>
              </details>
            )}
            {result.failed.length > 0 && (
              <div className="mt-3 text-sm bg-red-50 border border-red-200 text-red-800 rounded-lg p-3" data-testid="reissue-failed">
                <div className="font-semibold mb-1">No se pudieron reinvitar:</div>
                <ul className="list-disc pl-5">{result.failed.map((r) => <li key={r.supplier_id}>{r.supplier_name}</li>)}</ul>
              </div>
            )}
            <button onClick={onClose} className="btn-primary text-sm mt-4" data-testid="reissue-done">Listo</button>
          </div>
        )}
      </div>
    </div>
  );
}
